import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { API_BASE_URL } from "../config/api";
import { resolveImageUrl } from "../utils/image";
import "./CartPage.css";

const CART_STORAGE_KEY = "rungdo_cart_v1";
const WHATSAPP_NUMBER = "923147864360";
const STRIPE_CHECKOUT_ENDPOINT = `${API_BASE_URL}/api/payments/create-checkout-session`;

export default function CartPage() {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [checkoutError, setCheckoutError] = useState("");
  const [isStripeLoading, setIsStripeLoading] = useState(false);

  const loadCart = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(CART_STORAGE_KEY) || "[]");
      setCartItems(Array.isArray(saved) ? saved : []);
    } catch (error) {
      setCartItems([]);
    }
  };

  const persistCart = (nextItems) => {
    setCartItems(nextItems);
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(nextItems));
    window.dispatchEvent(new Event("cart-updated"));
  };

  useEffect(() => {
    loadCart();
    const handleUpdate = () => loadCart();
    window.addEventListener("storage", handleUpdate);
    window.addEventListener("cart-updated", handleUpdate);
    return () => {
      window.removeEventListener("storage", handleUpdate);
      window.removeEventListener("cart-updated", handleUpdate);
    };
  }, []);

  const subtotal = useMemo(
    () =>
      cartItems.reduce(
        (total, item) => total + (Number(item.price) || 0) * (Number(item.quantity) || 1),
        0
      ),
    [cartItems]
  );

  const totalUnits = useMemo(
    () => cartItems.reduce((count, item) => count + (Number(item.quantity) || 1), 0),
    [cartItems]
  );

  const updateQty = (index, delta) => {
    const nextItems = [...cartItems];
    const currentQty = Number(nextItems[index].quantity) || 1;
    const nextQty = currentQty + delta;
    if (nextQty <= 0) {
      nextItems.splice(index, 1);
    } else {
      nextItems[index] = { ...nextItems[index], quantity: nextQty };
    }
    persistCart(nextItems);
  };

  const removeItem = (index) => {
    const nextItems = cartItems.filter((_, i) => i !== index);
    persistCart(nextItems);
  };

  const clearCart = () => {
    persistCart([]);
  };

  const checkoutWithStripe = async () => {
    if (!cartItems.length) return;

    setCheckoutError("");
    setIsStripeLoading(true);

    try {
      const payload = {
        items: cartItems.map((item) => ({
          productId: item.productId || "",
          name: item.name,
          size: item.size || "",
          colorName: item.colorName || "",
          quantity: Number(item.quantity) || 1,
          unitPrice: Number(item.price) || 0,
          image: item.image || "",
          slug: item.slug || "",
        })),
        successUrl: `${window.location.origin}/checkout/success`,
        cancelUrl: `${window.location.origin}/checkout/cancel`,
      };

      const res = await axios.post(STRIPE_CHECKOUT_ENDPOINT, payload);
      const checkoutUrl = res?.data?.url;

      if (!checkoutUrl) {
        throw new Error("Stripe checkout URL missing");
      }

      window.location.href = checkoutUrl;
    } catch (error) {
      setCheckoutError("Stripe checkout is not available in your country. Please use WhatsApp checkout.");
    } finally {
      setIsStripeLoading(false);
    }
  };

  const checkoutOnWhatsApp = () => {
    if (!cartItems.length) return;

    const lines = [
      "Hi, I want to order these paint products:",
      ...cartItems.map((item, idx) => {
        const qty = Number(item.quantity) || 1;
        const amount = (Number(item.price) || 0) * qty;
        return `${idx + 1}. ${item.name} | ${item.size || "N/A"} | ${item.colorName || "N/A"} | Qty ${qty} | Rs ${amount}`;
      }),
      `Subtotal: Rs ${subtotal}`,
      `Website: https://www.rungdo.com`,
    ];

    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="cart-page">
      <Navbar />
      <main className="cart-main">
        <div className="cart-header">
          <h1>Your Cart</h1>
          <p>Review your products and place the order.</p>
          <div className="cart-header-stats">
            <div className="cart-stat">
              <span>Total Items</span>
              <strong>{totalUnits}</strong>
            </div>
            <div className="cart-stat">
              <span>Subtotal</span>
              <strong>Rs {subtotal}</strong>
            </div>
          </div>
        </div>

        {cartItems.length === 0 ? (
          <section className="cart-empty">
            <h2>Your cart is empty</h2>
            <p>Add products from e-store to continue.</p>
            <button type="button" onClick={() => navigate("/e-store")} className="cart-primary-btn">
              Browse Products
            </button>
          </section>
        ) : (
          <section className="cart-layout">
            <div className="cart-list">
              {cartItems.map((item, idx) => {
                const qty = Number(item.quantity) || 1;
                const unitPrice = Number(item.price) || 0;
                return (
                  <article className="cart-item" key={`${item.slug || item.name}-${idx}`}>
                    <div className="cart-item-media">
                      {item.image ? (
                        <img src={resolveImageUrl(item.image)} alt={item.name} />
                      ) : (
                        <div className="cart-item-fallback">No Image</div>
                      )}
                    </div>
                    <div className="cart-item-body">
                      <h3>{item.name}</h3>
                      <p className="item-meta"><span>Size</span>{item.size || "N/A"}</p>
                      <p className="item-meta"><span>Color</span>{item.colorName || "N/A"}</p>
                      <p className="item-meta"><span>Unit Price</span>Rs {unitPrice}</p>
                    </div>
                    <div className="cart-item-actions">
                      <div className="qty-controls">
                        <button type="button" onClick={() => updateQty(idx, -1)}>
                          -
                        </button>
                        <span>{qty}</span>
                        <button type="button" onClick={() => updateQty(idx, 1)}>
                          +
                        </button>
                      </div>
                      <p className="item-total">Rs {unitPrice * qty}</p>
                      <div className="cart-item-links">
                        <Link to={`/product/${item.slug}`} className="view-link">
                          View Product
                        </Link>
                        <button type="button" className="remove-btn" onClick={() => removeItem(idx)}>
                          Remove
                        </button>

                      </div>
                    </div>
                  </article>
                );
              })}
            </div>

            <aside className="cart-summary">
              <h3>Order Summary</h3>
              <div className="summary-row">
                <span>Items</span>
                <span>{totalUnits}</span>
              </div>
              <div className="summary-row">
                <span>Products</span>
                <span>{cartItems.length}</span>
              </div>
              <div className="summary-row">
                <span>Subtotal</span>
                <strong>Rs {subtotal}</strong>
              </div>
              <button
                type="button"
                className="cart-primary-btn"
                onClick={checkoutWithStripe}
                disabled={isStripeLoading}
              >
                {isStripeLoading ? "Redirecting to Stripe..." : "Checkout with Card (Stripe)"}
              </button>
              <button type="button" className="cart-secondary-btn" onClick={checkoutOnWhatsApp}>
                Checkout on WhatsApp
              </button>
              <button type="button" className="cart-tertiary-btn" onClick={() => navigate("/e-store")}>
                Continue Shopping
              </button>
              <button type="button" className="cart-ghost-btn" onClick={clearCart}>
                Clear Cart
              </button>
              {checkoutError && <p className="checkout-error">{checkoutError}</p>}
            </aside>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
